import Table from "src/features/fields/components/Table";
import { SettledSettlementColumns } from "../columns";
import Input from "src/features/fields/components/Input";
import { Autocomplete } from "src/components";
import { Fragment, useState, useEffect, useRef } from "react";
import Button from "src/features/fields/components/Button";
import { Transition } from "@headlessui/react";
import { SortingState } from "@tanstack/react-table";
import SettlementFilter from "src/features/fields/container/SettlementFilter";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import FilterIcon from "src/assets/filter.svg";
import axios from "axios";
import { useToast } from "src/hooks/useToast";
import { Download } from "lucide-react";

const searchTypeOptions = [
  { value: "ORDER_NUMBER", label: "Order Number" },
  { value: "INVOICE_NUMBER", label: "Invoice Number" },
  { value: "PO_NUMBER", label: "PO Number" },
  {
    value: "COMPANY_INVOICE_NUMBER",
    label: "Company Invoice Number",
  },
]

function SearchSettlement() {
  const { settlementFiltersData, filters } = useSelector((state: RootState) => state.settlement as any);
  const { SEARCH } = filters;

  const [data, setData] = useState([]);
  const [isFiltersShown, setIsFiltersShown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [searchType, setSearchType] = useState(searchTypeOptions[0]);
  const [searchText, setSearchText] = useState("");
  const [sorting, setSorting] = useState<SortingState>();

  const ref = useOutsideClick(() => setIsFiltersShown(false));
  const { toast } = useToast();
  const requestControllerRef = useRef<AbortController | null>(null);

  const getFilterValue = (filterArray: any[]) => {
    return Array.isArray(filterArray) && filterArray.length ? filterArray[0] : null;
  };

  const buildRequestBody = (searchText: string, searchType: any) => {
    const baseBody = {
      PageNo: "1",
      PageSize: "5000",
      Search_Text: searchText,
      Search_Type: searchType.value,
    };

    let filters = SEARCH;

    if (!filters) return baseBody;

    const company = getFilterValue(filters.company);
    const stockist = getFilterValue(filters.stockist);

    if (!company || !stockist || !searchText || !searchType) {
      return null;
    }

    const requestBody = {
      ...baseBody,
      UserUcode: stockist?.uid || "",
      company_code: company?.code || "", //M10-14192
    };

    return requestBody;
  };

  const abortPreviousRequest = () => {
    if (requestControllerRef.current) {
      requestControllerRef.current.abort();
    }
  };

  const fetchSearchSettlementData = async (searchText: string, searchType: any) => {
    abortPreviousRequest();
    const controller = new AbortController();
    requestControllerRef.current = controller;

    const api = import.meta.env.VITE_V1_STOCKIST_API;
    const apiEndpoint = `${api}/Settlement/GetSettlementCustom`

    try {
      setIsLoading(true);
      const requestBody = buildRequestBody(searchText, searchType);

      if (!requestBody) {
        toast({ variant: "default", description: "Company and Stockist filters are mandatory. A search text and search type are also required." });
        setIsLoading(false);
        return;
      }

      const response = await axios.post(
        apiEndpoint,
        requestBody, {
        signal: controller.signal,
      }
      );
      setData(response.data.result || []);
    } catch (error: any) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        console.error("Duplicate request cancelled:", error);
        return;
      }
      console.error("Error fetching settlement search data:", error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchSearchSettlementData(searchText, searchType);
    return () => {
      abortPreviousRequest();
    };
  }, [sorting, searchText, searchType, SEARCH]);

  let timerId: any;
  const handleSearch = (searchQuery: string) => {
    if (timerId) clearTimeout(timerId);
    timerId = setTimeout(() => {
      setSearchText(searchQuery);
    }, 500);
  };

  const fetchSettlementDataForDownload = async () => {
    const api = import.meta.env.VITE_V1_STOCKIST_API;
    const apiEndpoint = `${api}/Settlement/GetSettlementCustom`;

    const requestBody = buildRequestBody(searchText, searchType);

    if (!requestBody) {
      toast({ variant: "default", description: "Company and Stockist filters are mandatory. A search text and search type are also required." });
      setIsLoading(false);
      return;
    }

    setIsDownloading(true)
    try {
      const response = await axios.post(apiEndpoint, requestBody);
      return response?.data?.result || [];
    } catch (error) {
      console.error("Download API failed:", error);
      return [];
    }
    finally {
      setIsDownloading(false)
    }
  };

  const convertToCSV = (arr: any[]) => {
    if (!arr || !arr.length) return "";

    const keys = Object.keys(arr[0]);

    const csvRows = [
      keys.join(","), // header
      ...arr.map((obj) =>
        keys
          .map((k) => {
            const val =
              obj[k] === null || obj[k] === undefined
                ? ""
                : String(obj[k]);
            return `"${val.replace(/"/g, '""')}"`;
          })
          .join(",")
      ),
    ];

    return csvRows.join("\r\n");
  };

  const handleDownload = async () => {
    try {
      const results = await fetchSettlementDataForDownload();

      if (!results.length) {
        toast({
          variant: "default",
          description: "No data available to download.",
        });
        return;
      }

      const csv = convertToCSV(results);

      const blob = new Blob([csv], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        "custom_settlement.csv"
      );

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  return (
    <div className="p-6 flex flex-col gap-4">
      <Transition
        as={Fragment}
        show={isFiltersShown}
        enter="transition transition ease-in-out duration-200 transform"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition  ease-in-out duration-200 transform"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div className="fixed right-0 inset-0 top-0 z-[500]">
          <SettlementFilter
            ref={ref}
            type="SEARCH"
            companyDetails={settlementFiltersData?.company || []}
            onCloseClick={() => setIsFiltersShown(false)}
          />
        </div>
      </Transition>

      <div className="flex items-center justify-end gap-3 w-full">
        <Button
          onClick={handleDownload}
          className="gap-1 px-4 mt-2 ml-2 border relative"
          isLoading={isDownloading}
          loaderSize="small"
        >
          <Download size={18} />
        </Button>
        <div className="w-48">
          <Autocomplete
            onSelectionChange={(value: any) => {
              console.log("value", value);
              setSearchType(value);
            }}
            placeholder="Select"
            inputClassName="w-full"
            suggestionList={searchTypeOptions}
            displayKey="label"
            defaultValue={searchType}
            uniqueKey="value"
          />
        </div>

        <div className="w-96">
          <Input
            placeholder="Search by Order/PO Number, Company/Stockist Invoice Number"
            onChange={(e: any) => handleSearch(e.target.value)}
            className="w-full"
            showIcon={true}
          />
        </div>

        <Button
          className="gap-1 px-4 border relative"
          onClick={() => setIsFiltersShown(true)}
        >
          Filters
          <img src={FilterIcon} className="w-5 h-5" />
          {SEARCH?.filterCount > 0 && (
            <span className="absolute top-0 right-1 flex items-center justify-center w-5 h-5 text-xs text-white bg-red-500 rounded-full">
              {SEARCH.filterCount}
            </span>
          )}
        </Button>

        <Button
          onClick={() =>
            fetchSearchSettlementData(searchText, searchType)
          }
          loaderSize="small"
          className="py-2 border"
        >
          Retrieve
        </Button>
      </div>

      <Table
        headClass="bg-[#dee2ff] text-[#586AF5]"
        columns={SettledSettlementColumns}
        data={data ?? []}
        isLoading={isLoading}
        isGlobalSearch={true}
        isPaginating={false}
        sorting={sorting}
        onSortingChange={setSorting}
        manualSorting={true}
      />
    </div>
  );
}

export default SearchSettlement;